import { Component, DestroyRef, OnInit, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { InteractionService } from "../../services/interaction.service";
import { IInteraction } from "../../../core-ui/models/IInteraction";
import { ConversationTypeEnum } from "../../../core-ui/core-ui.enums";
import { AnalyticsService } from "../../../core-ui/services/analytics.service";
import { ApplicationService } from "../../../core-ui/services/application.service";
import { Router } from "@angular/router";
import { sessionStore } from "@auvious/utils";
import { CobrowseService } from "../../../core-ui/services/cobrowse.service";
import { KEY_COBROWSE_INTERACTION } from "../../app.enums";
import { SketchService } from "../../../core-ui/services/sketch.service";
import { GenericErrorHandler } from "../../services/error-handlers.service";
import { BasePage } from "../../../core-ui/models/BasePage";
import { merge } from "rxjs";

@Component({
  selector: "app-page-cobrowse",
  templateUrl: "./cobrowse.component.html",
  styleUrls: ["./cobrowse.component.scss"],
  standalone: false,
})
export class CobrowsePageComponent
  extends BasePage
  implements OnInit
{
  private interactionService = inject(InteractionService);
  private analyticsService = inject(AnalyticsService);
  private applicationService = inject(ApplicationService);
  private cobrowseService = inject(CobrowseService);
  private sketchService = inject(SketchService);
  private router = inject(Router);
  private errorHandler = inject(GenericErrorHandler);
  private destroyRef = inject(DestroyRef);

  interaction: IInteraction;
  isTalkdeskApp = false;
  private sketchId: string;

  constructor() {
    super();
    this.isTalkdeskApp = this.applicationService.isTalkdeskApp;
  }

  ngOnInit(): void {
    this.interaction = this.interactionService.getActiveInteraction();
    this.registerConversationEventHandlers();
  }

  async cobrowseEnded() {
    // todo: check if interaction is something else ?
    switch (this.interaction?.getType()) {
      case ConversationTypeEnum.cobrowse:
        // todo: if we have a genesys interaction active as a co-browse, set 'false' to 'true' (track in genesys)
        this.analyticsService.trackCallEnded(this.interaction, false);
        this.interactionService.clearActiveInteraction();
        break;
      case ConversationTypeEnum.callback:
      case ConversationTypeEnum.chat:
        // clear room
        this.interaction.setRoom(null);
        // clear invitation sent in case we want to escalate to video later on
        this.interaction.setCustomerInvited(false);
        this.analyticsService.trackCallEnding(this.interaction);
        this.interactionService.setActiveInteraction(this.interaction);
        try {
          this.activity.loading(true, "Ending co-browse...");
          await Promise.race([
            this.analyticsService.trackConferenceRoomEnded(this.interaction),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error("Timeout")), 3000)
            ),
          ]);
        } catch (ex) {
        } finally {
          this.activity.loading(false);
        }
        break;
    }
    this.leave();
  }

  leave() {
    sessionStore.removeItem(KEY_COBROWSE_INTERACTION);
    this.router.navigate(["/", "welcome"], { queryParamsHandling: "preserve" });
  }

  registerConversationEventHandlers() {
    if (this.applicationService.getActiveApplication().supportsInteractions()) {
      merge(
        this.interactionService.eventClient.ending$,
        this.interactionService.eventClient.transferring$
      ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.end());
    }
  }

  sketchConnected(connectionId: string) {
    this.sketchId = connectionId;
  }

  private reset() {
    this.interaction = null;
    this.interactionService.clearActiveInteraction();
  }

  private end() {
    try {
      // terminate cobrowse, will send a terminate request that sketch will get to clean up.
      this.cobrowseService.terminate();
    } catch (ex) {
      this.errorHandler.handleError(ex);
    }
    try {
      this.sketchService.terminate(this.sketchId);
    } catch (ex) {
      this.errorHandler.handleError(ex);
    }
    switch (this.interaction.getType()) {
      case ConversationTypeEnum.cobrowse:
        // wait for a cobrowseEnded event to track that it ended
        break;
      case ConversationTypeEnum.callback:
      case ConversationTypeEnum.chat:
        this.analyticsService.trackCallEnded(this.interaction, true);
        this.reset();
    }
    this.activity.loading(true, "Ending co-browse...");
  }
}
